import { ROLES } from '../context/AuthContext';

/** Substring in API 403 `detail` when the account is inactive (customer portal). */
export const PENDING_APPROVAL_DETAIL_HINT = 'pending administrator approval';

/** verify-otp / switch-account 403 `error` when no active account can log in. */
export const ACCOUNT_INACTIVE_ERROR = 'Account inactive';

/**
 * @param {number|undefined} status
 * @param {object|undefined|null} data
 * @returns {boolean}
 */
export function isAccountInactiveApiResponse(status, data) {
  if (status === 403 && data && data.error === ACCOUNT_INACTIVE_ERROR) return true;
  if (status === 400 && data && data.user_id) {
    const msgs = data.user_id;
    const arr = Array.isArray(msgs) ? msgs : [String(msgs)];
    return arr.some((m) => typeof m === 'string' && /inactive/i.test(m));
  }
  return false;
}

/**
 * Backend account lists for OTP login / switching should only include active accounts.
 *
 * @param {Array<{ is_active?: boolean }>|null|undefined} accounts
 * @returns {Array<{ is_active?: boolean }>}
 */
export function filterActiveCustomerAccounts(accounts) {
  if (!Array.isArray(accounts)) return [];
  return accounts.filter((account) => account.is_active !== false);
}

/**
 * @param {string|undefined} detail
 * @returns {boolean}
 */
export function isPendingApproval403Detail(detail) {
  if (detail == null) return false;
  const s = typeof detail === 'string' ? detail : String(detail);
  return s.toLowerCase().includes(PENDING_APPROVAL_DETAIL_HINT);
}

/**
 * Resolve role names from AuthContext state or embedded user payload (verify/login).
 * Permissions fetch can clear `userRoles` for inactive customers; `user.roles` remains.
 *
 * @param {{ roles?: Array<string|{ name?: string }> }|null|undefined} user
 * @param {string[]|null|undefined} userRoles
 * @returns {string[]}
 */
export function resolveCustomerRoles(user, userRoles) {
  if (Array.isArray(userRoles) && userRoles.length > 0) {
    return userRoles;
  }
  if (user && Array.isArray(user.roles) && user.roles.length > 0) {
    return user.roles.map((role) => (typeof role === 'string' ? role : role.name));
  }
  return Array.isArray(userRoles) ? userRoles : [];
}

/**
 * @param {string[]|null|undefined} userRoles
 * @param {{ roles?: Array<string|{ name?: string }> }|null|undefined} [user]
 * @returns {boolean}
 */
export function userHasCustomerRole(userRoles, user) {
  return resolveCustomerRoles(user, userRoles).includes(ROLES.CUSTOMER);
}

/**
 * Customer portal user waiting for admin activation (Django `is_active`).
 * Staff/non-customer users are never "pending" here.
 *
 * @param {{ is_active?: boolean }|null|undefined} user
 * @param {string[]|null|undefined} userRoles
 */
export function isCustomerPendingApproval(user, userRoles) {
  if (!userHasCustomerRole(userRoles, user)) return false;
  return user != null && user.is_active === false;
}

/**
 * Portal customer: only the `customer` role and no permissions from the API.
 * These accounts must use `/customer-*` routes, not the staff app (`/home`, etc.).
 *
 * @param {string[]|null|undefined} userRoles
 * @param {string[]|null|undefined} userPermissions
 * @param {{ roles?: Array<string|{ name?: string }> }|null|undefined} [user]
 * @returns {boolean}
 */
export function isCustomerPortalOnlyUser(userRoles, userPermissions, user) {
  const roles = resolveCustomerRoles(user, userRoles);
  if (roles.length === 0 || !roles.every((role) => role === ROLES.CUSTOMER)) {
    return false;
  }
  const perms = Array.isArray(userPermissions) ? userPermissions : [];
  return perms.length === 0;
}

/**
 * @param {string[]|null|undefined} userRoles
 * @param {string[]|null|undefined} userPermissions
 * @param {{ roles?: Array<string|{ name?: string }> }|null|undefined} [user]
 * @param {{ staffPath?: string, customerPath?: string }} [options]
 * @returns {string}
 */
export function resolvePostLoginRedirect(
  userRoles,
  userPermissions,
  user,
  { staffPath = '/home', customerPath = '/customer-home' } = {},
) {
  return isCustomerPortalOnlyUser(userRoles, userPermissions, user)
    ? customerPath
    : staffPath;
}
